import React from 'react';
import { Button } from '@/components/ui/button';
import { 
    cn, 
    formatDate, 
    priorityConfig, 
    isOverdue,
    formatDuration,
} from '@/lib/utils';
import {
    CheckCircle2,
    Clock,
    Calendar,
    Play,
    Square,
} from 'lucide-react';
const TaskListItem = ({ task, onClick, onStatusChange, onStartTimer, onStopTimer, isTimerActive }) => {
    const priorityStyle = priorityConfig[task.priority];
    const overdue = isOverdue(task.due_date) && task.status !== 'Done';

    return (
        <div 
            className={cn(
                "flex items-center gap-2 sm:gap-4 p-3 sm:p-4 cursor-pointer transition-all",
                isTimerActive 
                    ? "bg-brand-primary/10 border-l-4 border-l-brand-primary animate-pulse" 
                    : task.category === 'DAILY_TASK'
                    ? "border-l-4 border-l-blue-300 hover:bg-blue-50/30"
                    : "hover:bg-brand-accent-soft/30"
            )}
            onClick={onClick}
            data-testid={`my-task-${task.id}`}
        >
            {/* Status Checkbox */}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    const isDone = task.status === 'Done';
                    const isTodo = task.status === 'To Do';
                    onStatusChange(task.id, isDone ? 'To Do' : isTodo ? 'Review' : 'Done');
                }}
                className={cn(
                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0',
                    task.status === 'Done' 
                        ? 'bg-brand-success border-brand-success' 
                        : 'border-brand-border hover:border-brand-primary'
                )}
            >
                {task.status === 'Done' && (
                    <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={2} />
                )}
            </button>

            {/* Task Code - Hidden on mobile */}
            <span className="hidden sm:inline text-xs font-mono text-brand-primary font-semibold flex-shrink-0">
                #{task.task_code || task.task_number}
            </span>
            {task.category === 'DAILY_TASK' && (
                <span className="hidden sm:inline text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 font-medium flex-shrink-0">
                    Daily
                </span>
            )}

            {/* Title & Info */}
            <div className="flex-1 min-w-0">
                <p className={cn(
                    'font-medium text-sm truncate',
                    task.status === 'Done' ? 'text-brand-text-muted line-through' : 'text-brand-text-primary'
                )}>
                    {task.title}
                    {isTimerActive && (
                        <span className="ml-1 sm:ml-2 text-xs text-brand-primary font-normal">(Timer)</span>
                    )}
                </p>
                {/* Mobile: Show status and due date inline */}
                <div className="flex items-center gap-2 mt-0.5 sm:hidden">
                    <span className={cn(
                        'text-xs px-1.5 py-0.5 rounded',
                        task.status === 'Done' ? 'bg-brand-success/10 text-brand-success' :
                        task.status === 'In Progress' ? 'bg-brand-info/10 text-brand-info' :
                        'bg-brand-background text-brand-text-secondary'
                    )}>
                        {task.status}
                    </span>
                    {task.due_date && (
                        <span className={cn(
                            'text-xs',
                            overdue ? 'text-brand-error' : 'text-brand-text-muted'
                        )}>
                            {formatDate(task.due_date)}
                        </span>
                    )}
                </div>
                {task.time_logged > 0 && (
                    <p className="hidden sm:flex text-xs text-brand-text-muted items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {formatDuration(task.time_logged)} logged
                    </p>
                )}
            </div>

            {/* Priority - Hidden on mobile */}
            {task.priority !== 'none' && (
                <span className={cn('hidden sm:inline text-xs px-2 py-1 rounded-full flex-shrink-0', priorityStyle.bgClass)}>
                    {priorityStyle.label}
                </span>
            )}

            {/* Status Badge - Hidden on mobile (shown in title area) */}
            <span className={cn(
                'hidden sm:inline text-xs px-2 py-1 rounded-full flex-shrink-0',
                task.status === 'Done' ? 'bg-brand-success/10 text-brand-success' :
                task.status === 'In Progress' ? 'bg-brand-info/10 text-brand-info' :
                'bg-brand-background text-brand-text-secondary'
            )}>
                {task.status}
            </span>

            {/* Due Date - Hidden on mobile (shown in title area) */}
            {task.due_date && (
                <span className={cn(
                    'hidden sm:flex text-xs items-center gap-1 flex-shrink-0',
                    overdue ? 'text-brand-error' : 'text-brand-text-muted'
                )}>
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {formatDate(task.due_date)}
                </span>
            )}

            {/* Timer Button - Start or Stop */}
            {isTimerActive ? (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => onStopTimer(e, task)}
                >
                    <Square className="w-4 h-4" strokeWidth={1.5} />
                </Button>
            ) : (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 flex-shrink-0"
                    onClick={(e) => onStartTimer(e, task)}
                >
                    <Play className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
                </Button>
            )}
        </div>
    );
};


export default TaskListItem;